/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from "react";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";
import { EditableBlackoutDateRangeAccessRule } from "./blackoutDateRange";
import { useDateRangeValidation } from "../validation/dateRange/useDateRangeValidation";
import DateInput from "../../../forms/elements/DateInput";
import { getResource } from "../../../language/resources/getResource";
import { css, SerializedStyles } from "@emotion/react";
import { TimeRangeInput } from "../validation/timeRange/TimeRangeInput";
import { DateRangeIssue } from "../validation/dateRange/dateRange.types";
import trashcan from "../../../assets/icons/trashcan.svg";
import { EditableEffectiveDateRangeAccessRule } from "../effectiveDateRange/effectiveDateRange";
import { validationStyles } from "../validation/validationStyles";
import { ValidationIndicator } from "../validation/ValidationIndicator";
import {
  dashCss,
  dateTimeCss,
  deleteButtonCss,
  inputBaseCss,
  rowContainer,
} from "../ruleEditorStyles";
import { shouldShowSubmitValidationForRow } from "../validation/validationUtils";

const Dash = () => <span css={dashCss} />;

function mapDateRangeIssueToMessage(
  issue: DateRangeIssue,
  resources: Record<string, any>,
): string {
  switch (issue.code) {
    case "START_AND_END_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_START_AND_END_REQUIED",
      );

    case "START_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_START_DATE_REQUIRED",
      );

    case "END_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_END_DATE_REQUIRED",
      );

    case "START_AFTER_END":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_START_AFTER_END",
      );

    case "START_BEFORE_SOFT_MIN":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_START_BEFORE_EFFECTIVE_START",
      );

    case "END_AFTER_SOFT_MAX":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_END_AFTER_EFFECTIVE_END",
      );

    case "DATE_RANGE_OVERLAP":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_BLACKOUT_DATE_RANGE_OVERLAP",
      );

    default:
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_UNKNOWN_VALIDATION_ERROR",
      );
  }
}

function getDateInputBorderStyle(
  field: "start" | "end" | "range",
  issues: DateRangeIssue[],
): SerializedStyles | undefined {
  const fieldIssues = issues.filter(
    (issue) => issue.field === field || issue.field === "range",
  );

  if (fieldIssues.some((i) => i.severity === "error")) {
    return validationStyles.errorBorder;
  }

  if (fieldIssues.some((i) => i.severity === "warning")) {
    return validationStyles.warningBorder;
  }

  return undefined;
}

type Props = {
  resources: Record<string, any>;
  dateFormat: string;
  timeFormat: TimeFormat;
  rule: EditableBlackoutDateRangeAccessRule;
  effectiveDateRange?: EditableEffectiveDateRangeAccessRule;
  hasDateOverlap: boolean;
  onChange: (rule: EditableBlackoutDateRangeAccessRule | undefined) => void;
  onDelete: (rule: EditableBlackoutDateRangeAccessRule) => void;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
};

/**
 * Editing model: local draft state with parent reconciliation.
 *
 * Rows keep local state for user interaction and commit changes
 * upward via onChange / onDelete.
 *
 * The parent owns the source of truth, identity reconciliation,
 * and diffing (created / updated / deleted).
 *
 * Local state re-syncs only when the rule’s identity changes.
 */
export function BlackoutDateRangeRuleRow({
  resources,
  dateFormat,
  timeFormat,
  rule,
  effectiveDateRange,
  hasDateOverlap,
  onChange,
  onDelete,
  submitCount,
  hasChangesSinceSubmit,
}: Props) {
  const [localRuleId, setLocalRuleId] = useState(rule.localRuleId);

  // Local state for date/time inputs
  const [blackoutDateStart, setBlackoutDateStart] = useState<Date | null>(
    rule.blackoutDateStart,
  );

  const [blackoutDateEnd, setBlackoutDateEnd] = useState<Date | null>(
    rule.blackoutDateEnd,
  );

  const [blackoutTimeSlotStart, setBlackoutTimeSlotStart] =
    useState<Date | null>(rule.blackoutTimeSlotStart);

  const [blackoutTimeSlotEnd, setBlackoutTimeSlotEnd] = useState<Date | null>(
    rule.blackoutTimeSlotEnd,
  );

  // Update local state when parent rules changes
  useEffect(() => {
    setLocalRuleId(rule.localRuleId);
    setBlackoutDateStart(rule.blackoutDateStart);
    setBlackoutDateEnd(rule.blackoutDateEnd);
    setBlackoutTimeSlotStart(rule.blackoutTimeSlotStart);
    setBlackoutTimeSlotEnd(rule.blackoutTimeSlotEnd);

    // Intentional - only reset local state when the specific rule changes, not on every prop change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.liftAccessRuleId]);

  const blackoutDateRangeIsEmpty =
    blackoutDateStart === null && blackoutDateEnd === null;

  const showSubmitValidation = shouldShowSubmitValidationForRow(
    submitCount,
    hasChangesSinceSubmit,
    blackoutDateRangeIsEmpty,
  );

  useEffect(() => {
    // Trigger validation for untouched rows on save
  }, [submitCount, hasChangesSinceSubmit]);

  // Validate against effective date range rule
  const validation = useDateRangeValidation(
    blackoutDateStart,
    blackoutDateEnd,
    effectiveDateRange && {
      softMinStart: effectiveDateRange.effectiveDateStart ?? undefined,
      softMaxEnd: effectiveDateRange.effectiveDateEnd ?? undefined,
    },
  );

  const effectiveValidation = useMemo(() => {
    // Suppress validation ONLY if the row is empty AND
    // we are NOT showing submit-time validation
    if (blackoutDateRangeIsEmpty && !showSubmitValidation) {
      return {
        ...validation,
        issues: [],
        errors: [],
        warnings: [],
        primary: null,
        hasErrors: false,
        hasWarnings: false,
      };
    }

    // Add overlap errors
    const overlapIssues: DateRangeIssue[] = hasDateOverlap
      ? [
          {
            code: "DATE_RANGE_OVERLAP",
            field: "range",
            severity: "error",
          },
        ]
      : [];

    const issues = [...validation.issues, ...overlapIssues];
    const errors = issues.filter((i) => i.severity === "error");
    const warnings = issues.filter((i) => i.severity === "warning");

    return {
      ...validation,
      issues,
      errors,
      warnings,
      primary: errors[0] ?? warnings[0] ?? null,
      hasErrors: errors.length > 0,
      hasWarnings: warnings.length > 0,
    };
  }, [
    validation,
    hasDateOverlap,
    blackoutDateRangeIsEmpty,
    showSubmitValidation,
  ]);

  const startDateBorderCss = useMemo(
    () => getDateInputBorderStyle("start", effectiveValidation.issues),
    [effectiveValidation.issues],
  );

  const endDateBorderCss = useMemo(
    () => getDateInputBorderStyle("end", effectiveValidation.issues),
    [effectiveValidation.issues],
  );

  const primaryMessage = effectiveValidation.primary
    ? mapDateRangeIssueToMessage(effectiveValidation.primary, resources)
    : undefined;

  // Sync local state back to parent on changes after render
  const handleChange = useCallback(
    (patch: Partial<EditableBlackoutDateRangeAccessRule>) => {
      onChange({ ...rule, ...patch });
    },
    [onChange, rule],
  );

  const handleBlackoutDateStartChange = useCallback(
    (date: Date | null) => {
      const value = date ?? null;
      setBlackoutDateStart(value);
      handleChange({ blackoutDateStart: value });
    },
    [handleChange],
  );

  const handleBlackoutDateEndChange = useCallback(
    (date: Date | null) => {
      const value = date ?? null;
      setBlackoutDateEnd(value);
      handleChange({ blackoutDateEnd: value });
    },
    [handleChange],
  );

  const handleTimeSlotChange = useCallback(
    ({ start, end }: { start: Date | null; end: Date | null }) => {
      setBlackoutTimeSlotStart(start);
      setBlackoutTimeSlotEnd(end);
      handleChange({
        blackoutTimeSlotStart: start,
        blackoutTimeSlotEnd: end,
      });
    },
    [handleChange],
  );

  const ruleIdTitle = rule?.liftAccessRuleId
    ? getResource(resources, "ACCESS_RULE_EDIT_RULE_ID", rule.liftAccessRuleId)
    : getResource(resources, "ACCESS_RULE_EDIT_NEW_RULE");

  return (
    <div css={rowContainer}>
      <DateInput
        id={`${localRuleId}-blackout-date-start-input`}
        date={blackoutDateStart ?? undefined}
        onChange={handleBlackoutDateStartChange}
        placeholderText={getResource(resources, "ACCESS_RULE_EDIT_DATE_START")}
        tooltipText={ruleIdTitle}
        dateFormat={dateFormat}
        rootCss={css`
          margin-left: 5px;
          ${dateTimeCss};
          ${startDateBorderCss};
        `}
      />
      <Dash />
      <DateInput
        id={`${localRuleId}-blackout-date-end-input`}
        date={blackoutDateEnd ?? undefined}
        onChange={handleBlackoutDateEndChange}
        placeholderText={getResource(resources, "ACCESS_RULE_EDIT_DATE_END")}
        tooltipText={ruleIdTitle}
        dateFormat={dateFormat}
        rootCss={css`
          ${dateTimeCss};
          ${endDateBorderCss};
        `}
      />
      <ValidationIndicator
        severity={effectiveValidation?.primary?.severity}
        message={primaryMessage}
        visible={!!effectiveValidation.primary}
      />
      <TimeRangeInput
        id={rule?.localRuleId ?? ""}
        resources={resources}
        start={blackoutTimeSlotStart}
        startPlaceholder={getResource(resources, "ACCESS_RULE_EDIT_TIME_START")}
        end={blackoutTimeSlotEnd}
        endPlaceholder={getResource(resources, "ACCESS_RULE_EDIT_TIME_END")}
        title={ruleIdTitle}
        format={timeFormat}
        onChange={handleTimeSlotChange}
        showDash
        timeInputCss={css`
          ${dateTimeCss};
          ${inputBaseCss}
        `}
        rootCss={css`
          margin-left: 15px;
        `}
      />
      <button
        type="button"
        tabIndex={-1}
        css={deleteButtonCss}
        onClick={() => onDelete(rule)}
        title={getResource(resources, "ACCESS_RULE_DELETE")}
      >
        <img src={trashcan} alt="delete" />
      </button>
    </div>
  );
}
